import { DialogService } from './../../../../../shared/services/dialog-service';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FuseSplashScreenService } from '@barebone-fuse/@fuse/services/splash-screen.service';
import { CommandService } from '@core/services/command.service';
import { EliteFormFlowChartDataService } from '@features/elite-form/services/elite-form-flow-chart-data.service';
import { EliteFormManagerService } from '@features/elite-form/services/elite-form-manager.service';
import { EliteFormServiceFactory } from '@features/elite-form/services/elite-form-service-factory.service';
import { EliteFormValidationService } from '@features/elite-form/services/form-validation.service';
import { PlatformCommand, PlatformCommandResponse } from '@shared/commands/command-base';
import { DialogField, PlatformDialog } from '@shared/components/platform-tool/platform-tool-dialog-definition/models/dialog-defnition-models';
import { take, takeUntil } from 'rxjs';
import { FormResponseViewService } from "@features/elite-form/services/form-response-view.service";
import { Subject } from 'rxjs';
import { EliteformTableOfContentsService } from '@shared/components/platform-tool/platform-tool-dialog-definition/services/eliteform-table-of-contents.service';

@Component({
    standalone: false,
    selector: 'app-elite-form-group-editor-container',
  templateUrl: './elite-form-group-editor-container.component.html',
  styleUrl: './elite-form-group-editor-container.component.scss'
})
export class EliteFormGroupEditorContainerComponent implements OnInit, OnDestroy {
  @Input() eliteFormId: string = '';
  @Input() childFlowchartId: string = '';
  @Input() platformDialog: PlatformDialog;
  @Input() dialogField: DialogField;
  @Input() itemId: string;

  @Output() onDelete = new EventEmitter<boolean>();

  inputForm: FormGroup = new FormGroup({});
  ngUnsubscribe$ = new Subject<void>();

  eliteFormManagerService: EliteFormManagerService;
  autoSaveCommand: PlatformCommand;
  isExpanded: boolean = false;
  showDelete: any;

  groupErrors: number = 0;
  totalErrors: number = 0;

  constructor(
    private eliteFormFlowChartDataService: EliteFormFlowChartDataService,
    private eliteFormServiceFactory: EliteFormServiceFactory,
    private commandService: CommandService,
    private formResponseViewService: FormResponseViewService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private dialogService: DialogService,
    private validationService: EliteFormValidationService,
    private tocService: EliteformTableOfContentsService,
  ) { }

  ngOnInit(): void {
    // Regular groups are expanded by default so their fields are in the DOM and
    // navigable from the table of contents. Multi-response groups stay collapsed
    // (lazy) to avoid eagerly rendering/querying every response row.
    this.isExpanded = !this.dialogField.fieldValues['IsMultiResponseField'];

    // Expand this section when its matching TOC response item is expanded
    // (correlates by itemId === dataItemId).
    this.tocService.expandSection$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(itemId => {
        if (itemId === this.itemId) {
          this.isExpanded = true;
        }
      });

    this.validationService.registerFormGroup(this.platformDialog.id, this.inputForm, this.ngUnsubscribe$);

    this.validationService.getValidationState()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(state => {
        this.groupErrors = state.groupErrors[this.platformDialog.id] || 0;
        this.totalErrors = state.totalErrors;
      });

    this.eliteFormManagerService = this.eliteFormServiceFactory.getEliteFormManagerService(this.eliteFormId);

    this.autoSaveCommand = this.platformDialog.bottomPanelActions.find(command => command.name === "UpdateEliteFormDataCommand");
    this.autoSaveCommand.fieldValues['EliteFormId'] = this.eliteFormId;
    this.autoSaveCommand = this.formResponseViewService.initAutoSaveCommand(
      this.autoSaveCommand,
      this.eliteFormFlowChartDataService.responseId,
      this.eliteFormFlowChartDataService.schemaId,
      this.eliteFormFlowChartDataService.isClientSchema
    );
  }

  onFieldValueChange(event) {
    this.formResponseViewService.updateFieldValue(this.autoSaveCommand, event.fieldKey, event.fieldValue);
  }

  ngOnDestroy(): void {
    this.validationService.unregisterFormGroup(this.platformDialog.id);
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  changeExpand(isExpanded: boolean) {
    this.isExpanded = isExpanded;
  }

  get totalErrorCount() {
    return Object.keys(this.inputForm.controls).filter(o => this.inputForm.controls[o].errors && (this.inputForm.controls[o].touched || this.inputForm.controls[o].dirty)).length;
  }

  deleteSection(event: any) {
    event.stopPropagation();
    this.dialogService.showYesNoDialog("Delete Section", "Are you sure you want to delete this section?").pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response) => {
        if (response && response.action === 'OK') {
          this.fuseSplashScreenService.show();
          let deleteCommand = this.platformDialog.bottomPanelActions.find(command => command.name === "DeleteEliteFormMultiResponseCommand");
          deleteCommand.fieldValues["QuestionTitle"] = this.dialogField.title;
          this.commandService.executePlatformCommand(deleteCommand)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
              next: (response) => {
                this.eliteFormFlowChartDataService.refreshResponseItems(this.eliteFormId);
                this.onDelete.emit(true);
                this.formResponseViewService.updateTableOfContents();
              },
              error: () => { this.fuseSplashScreenService.hide() }
            });
        }
        this.fuseSplashScreenService.hide()
      }
    });
  }
}
